import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Undo2, RefreshCw, ScrollText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BASE, call, get, nz, Pill } from "./common";
import { JobLogDialog } from "./SchedulerPage";

const downloadReport = async (b) => {
  const r = await api.get(`${BASE}/batches/${b.id}/report.csv`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = `import-report-${b.id.slice(0, 8)}.csv`;
  a.click();
};

export default function HistoryPanel({ s, bump }) {
  const [batches, setBatches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logJob, setLogJob] = useState(null);
  const load = useCallback(() => Promise.all([get(`/suppliers/${s.id}/batches`), get("/jobs", { supplier_id: s.id, limit: 20 })])
    .then(([b, j]) => { setBatches(b); setJobs(j); }), [s.id]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load, bump]);
  const rollback = async (b, force = false) => {
    if (!force && !window.confirm(`Roll back this ${b.mode} import? New products it created are removed and changed fields are restored.`)) return;
    try {
      await api.post(`${BASE}/batches/${b.id}/rollback`, null, { params: force ? { force: true } : {} });
      toast.success("Rollback queued"); load();
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${e.response.data.detail}. Roll back anyway?`)) return rollback(b, true);
      if (e.response?.status !== 409) toast.error(e.response?.data?.detail || "Rollback failed");
    }
  };
  const errors = jobs.filter((j) => ["failed", "timed_out"].includes(j.status) || j.error);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <section className="rounded-xl border bg-white p-4" data-testid="supplier-import-history">
        <div className="flex items-center justify-between mb-3"><h3 className="font-bold">Import history</h3>
          <Button size="sm" variant="ghost" onClick={load} data-testid="supplier-history-refresh"><RefreshCw size={14} /></Button></div>
        {batches.length === 0 && <p className="text-sm text-muted-foreground">No imports yet.</p>}
        <div className="divide-y text-sm">
          {batches.map((b) => (
            <div key={b.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`batch-${b.id}`}>
              <Pill s={b.status} /> <span className="font-semibold capitalize">{b.mode}</span>
              <span className="text-muted-foreground text-xs">{nz(b.applied_at || b.created_at)} · {b.filename}</span>
              {b.counts && <span className="text-xs w-full text-muted-foreground">+{b.counts.create} new · {b.counts.update} updated · {b.counts.unchanged} unchanged · {b.counts.held_for_review} held · {b.counts.errors} errors</span>}
              <span className="flex gap-1 ml-auto">
                <Button size="sm" variant="outline" onClick={() => downloadReport(b)} data-testid={`batch-report-${b.id}`}><Download size={13} className="mr-1" />Report</Button>
                {["applied", "partial"].includes(b.status) && <Button size="sm" variant="outline" onClick={() => rollback(b)} data-testid={`batch-rollback-${b.id}`}><Undo2 size={13} className="mr-1" />Roll back</Button>}
              </span>
            </div>))}
        </div>
      </section>
      <section className="rounded-xl border bg-white p-4" data-testid="supplier-jobs">
        <h3 className="font-bold mb-3">Recent jobs {errors.length > 0 && <span className="text-xs text-red-600 font-semibold">· {errors.length} with errors</span>}</h3>
        {jobs.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
        <div className="divide-y text-sm">
          {jobs.map((j) => (
            <div key={j.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`supplier-job-${j.id}`}>
              <Pill s={j.status} /> <span className="font-semibold">{j.label}</span>
              <span className="text-xs text-muted-foreground">{nz(j.finished_at || j.started_at || j.created_at)} · {j.trigger}{j.duration_s ? ` · ${j.duration_s}s` : ""}</span>
              {j.progress?.total ? <span className="text-xs">{j.progress.done}/{j.progress.total}</span> : null}
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setLogJob(j.id)} data-testid={`supplier-job-log-${j.id}`}><ScrollText size={13} className="mr-1" />Log</Button>
              {j.error && <p className="w-full text-xs text-red-600">{j.error}</p>}
            </div>))}
        </div>
      </section>
      <JobLogDialog id={logJob} onClose={() => setLogJob(null)} />
    </div>
  );
}
